import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TroubleshootingSection = () => {
  const [expandedIssue, setExpandedIssue] = useState(null);

  const commonIssues = [
    {
      id: 1,
      title: 'Unable to login to account',
      category: 'Account Issues',
      devices: ['Mobile', 'Desktop'],
      solutions: [
        'Check your email and password combination',
        'Try resetting your password using the "Forgot Password" link',
        'Clear your browser cache and cookies',
        'Try using a different browser or device',
        'Contact support if the issue persists'
      ],
      relatedLinks: [
        { title: 'Password Reset Guide', url: '#' },
        { title: 'Account Recovery', url: '#' }
      ]
    },
    {
      id: 2,
      title: 'Vendor contact form not working',
      category: 'Booking Issues',
      devices: ['Mobile', 'Desktop'],
      solutions: [
        'Check your internet connection',
        'Ensure all required fields are filled',
        'Try refreshing the page and submitting again',
        'Disable browser extensions that might interfere',
        'Use the direct message feature as an alternative'
      ],
      relatedLinks: [
        { title: 'How to Contact Vendors', url: '#' },
        { title: 'Alternative Contact Methods', url: '#' }
      ]
    },
    {
      id: 3,
      title: 'Payment not processing',
      category: 'Payment Issues',
      devices: ['Mobile', 'Desktop'],
      solutions: [
        'Verify your payment information is correct',
        'Check if your card has sufficient funds',
        'Try a different payment method',
        'Ensure your billing address matches your card',
        'Contact your bank if the issue continues'
      ],
      relatedLinks: [
        { title: 'Payment Methods Guide', url: '#' },
        { title: 'Billing Support', url: '#' }
      ]
    },
    {
      id: 4,
      title: 'Mobile app crashes on startup',
      category: 'Technical Issues',
      devices: ['Mobile'],
      solutions: [
        'Force close the app and restart it',
        'Update the app to the latest version',
        'Restart your mobile device',
        'Clear the app cache in your phone settings',
        'Uninstall and reinstall the app if necessary'
      ],
      relatedLinks: [
        { title: 'Mobile App Guide', url: '#' },
        { title: 'App Store Support', url: '#' }
      ]
    },
    {
      id: 5,
      title: 'Photos not uploading correctly',
      category: 'Technical Issues',
      devices: ['Mobile', 'Desktop'],
      solutions: [
        'Check your internet connection speed',
        'Ensure image files are under 10MB',
        'Try uploading one photo at a time',
        'Use supported formats (JPG, PNG, HEIC)',
        'Clear browser cache and try again'
      ],
      relatedLinks: [
        { title: 'Photo Upload Guide', url: '#' },
        { title: 'Supported File Types', url: '#' }
      ]
    }
  ];

  const getCategoryColor = (category) => {
    const colorMap = {
      'Account Issues': 'bg-primary-100 text-primary',
      'Booking Issues': 'bg-secondary-100 text-secondary',
      'Payment Issues': 'bg-warning-100 text-warning',
      'Technical Issues': 'bg-danger-100 text-danger'
    };
    return colorMap[category] || 'bg-surface-100 text-text-secondary';
  };

  const getDeviceIcon = (device) => {
    return device === 'Mobile' ? 'Smartphone' : 'Monitor';
  };

  const toggleExpanded = (issueId) => {
    setExpandedIssue(expandedIssue === issueId ? null : issueId);
  };

  return (
    <section className="py-16 bg-surface-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
            Troubleshooting Guide
          </h2>
          <p className="text-lg text-text-secondary">
            Quick solutions for common technical issues with device-specific help
          </p>
        </div>
        
        <div className="space-y-4">
          {commonIssues?.map((issue) => (
            <div
              key={issue?.id}
              className="bg-white rounded-xl shadow-soft border border-border overflow-hidden"
            >
              <button
                onClick={() => toggleExpanded(issue?.id)}
                className="w-full px-6 py-4 text-left hover:bg-surface-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(issue?.category)}`}>
                        {issue?.category}
                      </span>
                      <div className="flex items-center space-x-1">
                        {issue?.devices?.map((device, index) => (
                          <Icon
                            key={index}
                            name={getDeviceIcon(device)}
                            size={14}
                            className="text-text-muted"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <Icon
                    name={expandedIssue === issue?.id ? 'ChevronUp' : 'ChevronDown'}
                    size={20}
                    className="text-text-muted"
                  />
                </div>
                
                <h3 className="font-heading font-semibold text-lg text-text-primary mt-2">
                  {issue?.title}
                </h3>
              </button>
              
              {expandedIssue === issue?.id && (
                <div className="px-6 pb-6">
                  <div className="border-t border-border pt-4">
                    <h4 className="font-medium text-text-primary mb-3">
                      Step-by-step solution:
                    </h4>
                    
                    <ol className="space-y-2 mb-6">
                      {issue?.solutions?.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </span>
                          <span className="text-text-secondary">{solution}</span>
                        </li>
                      ))}
                    </ol>
                    
                    {issue?.relatedLinks?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-text-primary mb-3">
                          Related articles:
                        </h4>
                        <div className="space-y-2">
                          {issue?.relatedLinks?.map((link, index) => (
                            <a
                              key={index}
                              href={link?.url}
                              className="flex items-center space-x-2 text-primary hover:text-primary-600 transition-colors"
                            >
                              <Icon name="ExternalLink" size={14} />
                              <span className="text-sm">{link?.title}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <div className="bg-info-50 rounded-xl p-6">
            <Icon name="AlertCircle" size={24} className="text-info mx-auto mb-3" />
            <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
              Still having issues?
            </h3>
            <p className="text-text-secondary mb-4">
              If these solutions don't work, our technical support team is here to help.
            </p>
            <button className="text-primary hover:text-primary-600 font-medium transition-colors">
              Contact Technical Support →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TroubleshootingSection;